import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { fetchData, postData } from '../../helpers/apiUtils';
import { containsHardcodedYear, YEAR_WARNING } from '../../helpers/mailingValidation';
import { NfaRecommendation, NfaPreviewResponse } from '../../helpers/nfaMailingData';

interface Props {
  mailing: NfaRecommendation;
  plannedDate: string;
  onBack: () => void;
}

const MailingPreview: FC<Props> = ({ mailing, plannedDate, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [htmlSource, setHtmlSource] = useState('');
  const [showSource, setShowSource] = useState(false);
  const [copied, setCopied] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  const [confirming, setConfirming] = useState(false);

  // Mailing metadata
  const [fromName, setFromName] = useState(mailing.from_name || '');
  const [previewText, setPreviewText] = useState(mailing.preview_text || '');

  // Placeholder state
  const [emailTag, setEmailTag] = useState('{EMAIL}');

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isFirstLoad = useRef(true);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError(null);

    const isKme = mailing.source_type === 'kme';

    const params = new URLSearchParams({
      planned_date: plannedDate,
      email_tag: emailTag,
    });
    if (subject) params.set('subject', subject);
    if (previewText) params.set('preview_text', previewText);
    if (!isKme) {
      params.set('source_type', mailing.source_type);
      params.set('source_id', String(mailing.source_id));
    }
    if (mailing.webinar_id) {
      params.set('webinar_id', mailing.webinar_id);
    }

    const previewUrl = isKme
      ? `/api/nfa-mailings/kme/preview?${params.toString()}`
      : `/api/nfa-mailings/preview?${params.toString()}`;

    const result = await fetchData<NfaPreviewResponse>(previewUrl);

    if (result.success && result.data) {
      setPreviewHtml(result.data.html);
      setHtmlSource(result.data.html);
      setSubject(result.data.subject);
      if (isFirstLoad.current) {
        setPreviewText(result.data.preview_text || '');
        isFirstLoad.current = false;
      }
    } else {
      setError(result.error || 'Fehler beim Laden der Vorschau');
    }
    setLoading(false);
  }, [
    mailing.source_type,
    mailing.source_id,
    mailing.webinar_id,
    plannedDate,
    subject,
    previewText,
    emailTag,
  ]);

  useEffect(() => {
    loadPreview();
  }, []); // Only on mount — use "Vorschau aktualisieren" for updates

  // Write HTML to iframe and auto-resize height
  useEffect(() => {
    if (iframeRef.current && previewHtml) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument;
      if (doc) {
        doc.open();
        doc.write(previewHtml);
        doc.close();
        // Auto-resize after content renders (images may still load)
        const resize = () => {
          try {
            const h = doc.documentElement?.scrollHeight || doc.body?.scrollHeight;
            if (h && h > 100) {
              iframe.style.height = `${h + 40}px`;
            }
          } catch {
            // ignore
          }
        };
        setTimeout(resize, 200);
        setTimeout(resize, 1000); // re-check after images load
      }
    }
  }, [previewHtml]);

  const handleCopy = async () => {
    try {
      const textToCopy = showSource ? htmlSource : previewHtml;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setSnackbar({
        open: true,
        message: 'HTML in Zwischenablage kopiert',
        severity: 'success',
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setSnackbar({
        open: true,
        message: 'Kopieren fehlgeschlagen',
        severity: 'error',
      });
    }
  };

  const hasYearError = containsHardcodedYear(subject) || containsHardcodedYear(previewText);

  const handleConfirmSend = async () => {
    if (hasYearError) {
      setSnackbar({
        open: true,
        message: 'Jahreszahl erkannt — bitte {DATE:Y} als Platzhalter verwenden',
        severity: 'error',
      });
      return;
    }
    setConfirming(true);

    // If user edited the source, use from-html endpoint
    const isEdited = htmlSource !== previewHtml;

    let sendResult;
    if (isEdited) {
      sendResult = await postData<{ id: number; nfa_send_code: string }>(
        '/api/nfa-mailings/sends/from-html',
        {
          source_type: mailing.source_type,
          source_id: mailing.source_id,
          webinar_id: mailing.webinar_id,
          kme_rss_guid: mailing.rss_guid || null,
          planned_send_date: plannedDate,
          subject,
          preview_text: previewText,
          from_name: fromName,
          final_html: htmlSource,
          email_tag: emailTag,
        },
      );
    } else {
      sendResult = await postData<{
        id: number;
        nfa_send_code: string;
        html: string;
      }>('/api/nfa-mailings/sends', {
        source_type: mailing.source_type,
        source_id: mailing.source_id,
        webinar_id: mailing.webinar_id,
        kme_rss_guid: mailing.rss_guid || null,
        planned_send_date: plannedDate,
        subject,
        preview_text: previewText,
        from_name: fromName,
        email_tag: emailTag,
      });
    }

    if (!sendResult.success || !sendResult.data) {
      setSnackbar({
        open: true,
        message: sendResult.error || 'Fehler beim Speichern',
        severity: 'error',
      });
      setConfirming(false);
      return;
    }

    const sendId = sendResult.data.id;

    // Confirm the send
    const confirmResult = await fetchData(`/api/nfa-mailings/sends/${sendId}/confirm`, {
      method: 'PUT',
    });

    if (confirmResult.success) {
      setSnackbar({
        open: true,
        message: `Mailing als gesendet markiert (ID: ${sendId})`,
        severity: 'success',
      });
    } else {
      setSnackbar({
        open: true,
        message: confirmResult.error || 'Fehler beim Bestätigen',
        severity: 'error',
      });
    }
    setConfirming(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={onBack}>
          Zurück
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" gap={3}>
        {/* Left: Preview */}
        <Box flex={7}>
          <Typography variant="subtitle2" mb={1}>
            Vorschau
          </Typography>
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: '#f5f5f5',
            }}
          >
            <iframe
              ref={iframeRef}
              title="NFA Mailing Preview"
              style={{
                width: '100%',
                height: '700px',
                border: 'none',
                backgroundColor: '#ffffff',
              }}
            />
          </Box>
        </Box>

        {/* Right: Placeholder form */}
        <Box flex={3}>
          <Typography variant="subtitle2" mb={1}>
            Platzhalter
          </Typography>

          <TextField
            label="Betreff"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            error={containsHardcodedYear(subject)}
            helperText={containsHardcodedYear(subject) ? YEAR_WARNING : undefined}
          />

          <TextField
            label="Absendername"
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            helperText="From Name im ESP, z.B. one-stock-letter.de"
          />

          <TextField
            label="Vorschautext"
            value={previewText}
            onChange={(e) => setPreviewText(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            error={containsHardcodedYear(previewText)}
            helperText={
              containsHardcodedYear(previewText)
                ? YEAR_WARNING
                : 'Preheader-Text in der E-Mail-Vorschau'
            }
          />

          <TextField
            label="E-Mail Merge Tag"
            value={emailTag}
            onChange={(e) => setEmailTag(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
            helperText="ESP-spezifischer Tag, z.B. *|EMAIL|* oder {{email}}"
          />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadPreview}
            fullWidth
            sx={{ mb: 3 }}
          >
            Vorschau aktualisieren
          </Button>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="caption" color="textSecondary" display="block" mb={2}>
            Versanddatum: {new Date(plannedDate).toLocaleDateString('de-DE')}
          </Typography>

          <Alert severity="info" sx={{ mb: 2, fontSize: '12px' }}>
            Tracking-Parameter sind in allen Links enthalten. Änderungen an Link-URLs können das
            Tracking beeinträchtigen.
          </Alert>

          <Button
            variant="outlined"
            onClick={() => setShowSource(!showSource)}
            fullWidth
            sx={{ mb: 1 }}
          >
            {showSource ? 'Vorschau anzeigen' : 'HTML-Quellcode anzeigen'}
          </Button>

          <Button
            variant="contained"
            startIcon={copied ? <CheckIcon /> : <CopyIcon />}
            onClick={handleCopy}
            fullWidth
            color={copied ? 'success' : 'primary'}
            sx={{ mb: 1 }}
          >
            {copied ? 'Kopiert!' : 'HTML kopieren'}
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmSend}
            disabled={confirming}
            fullWidth
            sx={{ mb: 1 }}
          >
            {confirming ? <CircularProgress size={20} color="inherit" /> : 'Als gesendet markieren'}
          </Button>

          <Button startIcon={<BackIcon />} onClick={onBack} fullWidth size="small">
            Zurück zur Auswahl
          </Button>
        </Box>
      </Box>

      {/* Source code editor (below preview) */}
      {showSource && (
        <Box mt={3}>
          <Typography variant="subtitle2" mb={1}>
            HTML-Quellcode (bearbeitbar)
          </Typography>
          <textarea
            value={htmlSource}
            onChange={(e) => setHtmlSource(e.target.value)}
            style={{
              width: '100%',
              height: '500px',
              fontFamily: 'monospace',
              fontSize: '12px',
              padding: '12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              resize: 'vertical',
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
            }}
          />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MailingPreview;
